'use client';

import { type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

// Simple SVG icons to avoid external dependencies
function AlertTriangleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-6 text-destructive"
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function RefreshCwIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-3"
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}

// Error boundary wrapper component
function ErrorBoundaryInner({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  const [hasError, setHasError] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      setHasError(true);
      setError(event.error || new Error(event.message || 'Unknown error'));
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (hasError) {
    if (fallback) return fallback;
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 text-center px-6">
        <div className="size-12 rounded-full bg-destructive/10 flex items-center justify-center">
          <AlertTriangleIcon />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium">Algo salió mal</p>
          <p className="text-xs text-muted-foreground max-w-xs">
            Ocurrió un error inesperado en esta sección. Podés intentar recargar la página.
          </p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-1.5 h-7 rounded-lg border border-border bg-background hover:bg-muted text-xs px-2.5 transition-colors"
        >
          <RefreshCwIcon />
          Recargar
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

// Import React for useState and useEffect
import React from 'react';

export default function ErrorBoundary({ children, fallback }: Props) {
  return <ErrorBoundaryInner fallback={fallback}>{children}</ErrorBoundaryInner>;
}
